<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLocaleRouting } from '../composables/useLocaleRouting'
import AdSlot from '../components/AdSlot.vue'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import SiteLogo from '../components/SiteLogo.vue'
import ThemeSwitcher from '../components/ThemeSwitcher.vue'

defineProps<{
  /**
   * i18n key for the current tool's name, e.g. `tools.subnetCalculator`, shown as
   * the page's <h1> beside the logo. Omit it when the page has its own <h1>, as
   * the homepage does.
   */
  toolNameKey?: string
  sourceUrl?: string
}>()

const { t } = useI18n()

// Keeps <html lang>, the visible language and the URL in agreement.
useLocaleRouting()
</script>

<template>
  <a class="skip-link" href="#main">{{ t('shell.skipToContent') }}</a>

  <div class="shell">
    <header class="shell__header">
      <div class="shell__brand">
        <SiteLogo />
        <h1 v-if="toolNameKey" class="shell__tool">{{ t(toolNameKey) }}</h1>
      </div>
      <div class="shell__controls">
        <slot name="controls" />
        <LanguageSwitcher />
        <ThemeSwitcher />
      </div>
    </header>

    <main id="main" class="shell__main">
      <slot />
    </main>

    <AdSlot placement="footer" />

    <footer class="shell__footer">
      <span>{{ t('shell.footer.privacy') }}</span>
      <a v-if="sourceUrl" :href="sourceUrl" rel="noopener">{{ t('shell.footer.sourceCode') }}</a>
    </footer>
  </div>
</template>

<style scoped>
.shell {
  max-width: var(--shell-max-width);
  margin: 0 auto;
  padding: var(--space-4);
}

.shell__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-border);
}

.shell__brand {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
}

/* A hairline between logo and tool name: they are two things, not one title. */
.shell__tool {
  margin: 0;
  padding-left: var(--space-4);
  border-left: 1px solid var(--color-border);
  font-size: var(--text-xl);
  font-weight: 600;
  line-height: 1.2;
}

/* On a phone the name wraps under the logo, where a divider would dangle. */
@media (max-width: 480px) {
  .shell__tool {
    flex-basis: 100%;
    padding-left: 0;
    border-left: 0;
  }
}

.shell__controls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.shell__main {
  padding: var(--space-6) 0;
}

.shell__footer {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.skip-link {
  position: absolute;
  left: -9999px;
}

.skip-link:focus {
  left: var(--space-4);
  top: var(--space-4);
  z-index: 10;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-sm);
}
</style>
